/**
 * A dica da casa — o balãozinho que explica o botão sem rótulo.
 *
 * Substitui o `title=` do navegador, que é feio (fonte do sistema, fundo
 * amarelado), lento (quase um segundo para aparecer) e mudo em quem navega pelo
 * teclado. Aqui a dica é do desenho, nasce em 300ms e aparece também no foco.
 *
 * É UMA instância para a mesa inteira, com ouvinte delegado no documento: quem
 * quer dica só escreve `data-dica="texto"` no elemento, sem embrulhar nada num
 * componente a mais. Isso importa porque quase toda dica mora num botão dentro
 * de um flex apertado — um <span> extra em volta mexeria no layout, e são mais
 * de quinhentos `data-dica` na tela.
 *
 * É por isso que aqui NÃO se usa `<TooltipTrigger>`: o gatilho do shadcn embrulha
 * um elemento por dica. O que se usa é o resto dele — `Tooltip` + `TooltipContent`
 * com `anchor`, ou seja, o mesmo balão, o mesmo bico e a mesma conta de colisão
 * do `@base-ui/react`, ancorados no elemento que está sob o ponteiro. Foi o que
 * apagou as cem linhas de `encaixar()` que viviam aqui: virar de lado quando não
 * cabe, escorregar sem passar da borda e manter o bico apontando o alvo é
 * trabalho da primitiva.
 *
 * `data-dica-lado` escolhe de que lado ela nasce — `"baixo"`, `"direita"` ou
 * `"esquerda"`; sem ele é em cima. O trilho da esquerda pede `"direita"`: ali o
 * balão em cima tapa o ícone de cima, que é o vizinho que a pessoa está
 * comparando. Não cabendo, ela vira para o lado oposto sozinha.
 *
 * O balão NÃO recebe ponteiro (`pointer-events-none`) e não nasce com botão
 * apertado. Sem as duas, ele atrapalha em vez de explicar: pousado em cima do
 * vizinho, o primeiro clique naquele vizinho morria no balão (só o dispensava),
 * e arrastando ele acendia sob o cursor em cada alvo do caminho.
 *
 * Dica não é nome acessível: botão só de ícone continua precisando de
 * `aria-label`. O elemento também ganha `aria-describedby` enquanto a dica está
 * na tela, para o leitor de tela ler o mesmo texto que o mouse mostra.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Tooltip, TooltipContent } from "@/ui/tooltip.tsx";

/* 300ms na PRIMEIRA: é o tempo de o ponteiro atravessar a barra sem acender
   tudo pelo caminho — com 120ms, passar o mouse de uma ponta à outra da mesa
   era uma fileira de balões piscando. Depois que uma dica apareceu, as
   VIZINHAS nascem na hora (a pessoa já está lendo a barra, esperar de novo a
   cada ícone é a barra ficando lenta), desde que o ponteiro chegue nelas em
   até 400ms depois de a anterior sumir. */
const ATRASO = 300;
const VIZINHA = 400;
const FOLGA = 8;
const ID = "dica-da-mesa";

/** O lado em português do desenho → o lado que a primitiva entende. */
const LADOS = { cima: "top", baixo: "bottom", direita: "right", esquerda: "left" } as const;

type Lado = keyof typeof LADOS;
export type Dica = { texto: string; lado: Lado };

/**
 * O que a dica diz e de que lado ela nasce.
 *
 * Separado do componente porque é a única parte que não é pintura nem evento: é
 * a leitura dos atributos, e é o que o teste prende. Lado desconhecido cai em
 * "cima" em vez de virar `undefined` no meio do posicionamento.
 */
export function dicaDe(elemento: Pick<Element, "getAttribute"> | null | undefined): Dica | null {
  const texto = elemento?.getAttribute("data-dica")?.trim();
  if (!texto) return null;
  const pedido = elemento?.getAttribute("data-dica-lado");
  return { texto, lado: pedido && pedido in LADOS ? (pedido as Lado) : "cima" };
}

/** Monte uma vez, na casca. Cuida de todo `data-dica` da tela. */
export function Dicas() {
  const [alvo, setAlvo] = useState<(Dica & { elemento: Element }) | null>(null);
  const relogio = useRef<ReturnType<typeof setTimeout> | null>(null);
  const marcado = useRef<Element | null>(null);
  /* Quando a última dica VISÍVEL sumiu — é o que decide se a próxima espera. */
  const sumiu = useRef(0);

  const esconder = useCallback(() => {
    if (relogio.current) clearTimeout(relogio.current);
    relogio.current = null;
    if (marcado.current) sumiu.current = performance.now();
    marcado.current?.removeAttribute("aria-describedby");
    marcado.current = null;
    setAlvo(null);
  }, []);

  const mostrar = useCallback(
    (elemento: Element, atraso: number) => {
      const dica = dicaDe(elemento);
      if (!dica) return;
      if (relogio.current) clearTimeout(relogio.current);
      relogio.current = setTimeout(() => {
        /* A lista pode ter rolado entre o mouse entrar e o balão nascer. */
        if (!elemento.isConnected) return esconder();
        elemento.setAttribute("aria-describedby", ID);
        marcado.current = elemento;
        setAlvo({ ...dica, elemento });
      }, atraso);
    },
    [esconder],
  );

  useEffect(() => {
    const sobre = (e: PointerEvent) => {
      /* No toque não existe "passar o mouse": a dica só atrapalharia o clique. */
      if (e.pointerType === "touch") return;
      /* Botão apertado é gesto em curso — arrastar cartão no quadro ou arquivo no
         acervo passa por cima de dezenas de alvos, e cada um acenderia um balão
         debaixo do cursor, justo onde a pessoa precisa ver onde vai soltar. */
      if (e.buttons) return void (marcado.current && esconder());
      const alvoNovo = (e.target as Element | null)?.closest?.("[data-dica]");
      /* Saiu para um lugar sem dica: cancela também o balão que ia nascer. */
      if (!alvoNovo) return void ((marcado.current || relogio.current) && esconder());
      if (alvoNovo === marcado.current) return;
      const vizinha = marcado.current || performance.now() - sumiu.current < VIZINHA;
      mostrar(alvoNovo, vizinha ? 0 : ATRASO);
    };
    /* Foco pelo teclado não espera: quem chegou com Tab já quer saber o que é. */
    const foco = (e: FocusEvent) => {
      /* Só foco de TECLADO: tocar num botão no celular também dá foco, e o
         balão nascia em cima do menu que o toque acabou de abrir. */
      if (!(e.target as Element | null)?.matches?.(":focus-visible")) return;
      const alvoNovo = (e.target as Element | null)?.closest?.("[data-dica]");
      if (alvoNovo) mostrar(alvoNovo, 0);
      else if (marcado.current) esconder();
    };
    const tecla = (e: KeyboardEvent) => e.key === "Escape" && esconder();

    document.addEventListener("pointerover", sobre, true);
    document.addEventListener("pointerdown", esconder, true);
    document.addEventListener("focusin", foco, true);
    document.addEventListener("keydown", tecla, true);
    /* Rolagem e redimensionamento movem o alvo debaixo do balão: some. */
    window.addEventListener("scroll", esconder, true);
    window.addEventListener("resize", esconder);
    return () => {
      document.removeEventListener("pointerover", sobre, true);
      document.removeEventListener("pointerdown", esconder, true);
      document.removeEventListener("focusin", foco, true);
      document.removeEventListener("keydown", tecla, true);
      window.removeEventListener("scroll", esconder, true);
      window.removeEventListener("resize", esconder);
      if (relogio.current) clearTimeout(relogio.current);
    };
  }, [esconder, mostrar]);

  return (
    <Tooltip open={Boolean(alvo)}>
      {alvo ? (
        <TooltipContent
          id={ID}
          anchor={alvo.elemento}
          side={LADOS[alvo.lado]}
          sideOffset={FOLGA}
          /* A pele do desenho: superfície escura (a mesma nos dois temas, ao
             contrário da tinta, que inverte), raio de controle, 12,5/600, e o
             texto podendo quebrar em linhas (`whitespace-pre-line`) porque
             algumas dicas da mesa são duas frases. O bico (o único filho com
             `data-side`) vem do registro em `bg-foreground`, e no escuro ele
             ficaria claro apontando de um balão escuro. */
          className="pointer-events-none max-w-[min(260px,80vw)] rounded-controle bg-escuro px-2.5 py-1.5 text-apoio leading-[1.4] font-semibold whitespace-pre-line text-sobre-escuro shadow-[0_10px_26px_-6px_color-mix(in_oklab,_var(--color-escuro)_35%,_transparent)] [&_[data-side]]:bg-escuro [&_[data-side]]:fill-escuro"
        >
          {alvo.texto}
        </TooltipContent>
      ) : null}
    </Tooltip>
  );
}
